import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

/* Design tokens mirrored from the version3 /auth design */
export const authTokens = `
  --auth-background: oklch(1 0 0);
  --auth-foreground: oklch(0.18 0.02 260);
  --auth-card: oklch(1 0 0);
  --auth-primary: oklch(0.703 0.198 43.4) /* #FF6B1A */;
  --auth-muted: oklch(0.97 0.005 260);
  --auth-muted-foreground: oklch(0.5 0.015 258);
  --auth-border: oklch(0.93 0.006 260);
  --auth-houdiny-from: var(--auth-primary);
  --auth-gradient-houdiny: linear-gradient(
    135deg,
    oklch(0.744 0.168 47.9) 0%,
    oklch(0.56 0.17 42) 48%,
    oklch(0.13 0.02 55) 100%
  );
  --auth-gradient-soft: linear-gradient(
    135deg,
    color-mix(in oklab, var(--auth-primary) 9%, transparent) 0%,
    color-mix(in oklab, var(--auth-foreground) 5%, transparent) 100%
  );
  --auth-shadow-card: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px -12px rgba(15, 23, 42, 0.08);
  --auth-shadow-elevated: 0 1px 2px rgba(15, 23, 42, 0.05), 0 30px 60px -24px rgba(15, 23, 42, 0.18);
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const AuthPage = styled.div`
  ${authTokens}
  display: flex;
  min-height: 100vh;
  background: var(--auth-background);
  color: var(--auth-foreground);
  font-family: 'Inter', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
  -webkit-font-smoothing: antialiased;
`;

export const AuthFormColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  padding: 48px 24px;

  @media (min-width: 1024px) {
    width: 52%;
    padding: 48px 64px;
  }
`;

export const AuthFormInner = styled.div`
  width: 100%;
  max-width: 448px;
  margin: 0 auto;
`;

export const AuthPreviewColumn = styled.div`
  display: none;
  border-left: 1px solid var(--auth-border);

  @media (min-width: 1024px) {
    display: block;
    width: 48%;
  }
`;

export const AuthLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  img {
    height: 36px;
    width: auto;
  }

  .aside {
    margin-left: auto;
    font-size: 12px;
    font-weight: 500;
    color: var(--auth-muted-foreground);
  }
`;

export const AuthTitle = styled.h1`
  && {
    margin: 36px 0 0;
    font-size: 30px;
    line-height: 36px;
    font-weight: 600;
    letter-spacing: -0.025em;
    color: var(--auth-foreground);
    text-transform: none;
  }
`;

export const AuthSubtitle = styled.p`
  && {
    margin: 10px 0 0;
    font-size: 14px;
    line-height: 1.625;
    color: var(--auth-muted-foreground);
  }
`;

export const SocialButtons = styled.div`
  margin-top: 32px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SocialButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
  height: 48px;
  border: 1px solid var(--auth-border);
  border-radius: 18px;
  background: var(--auth-card);
  font-size: 14px;
  font-weight: 500;
  color: var(--auth-foreground);
  box-shadow: var(--auth-shadow-card);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover:not(:disabled) {
    background: var(--auth-muted);
  }

  &:disabled {
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 28px 0;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--auth-border);
  }

  span {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--auth-muted-foreground);
  }
`;

export const AuthFormShell = styled.div`
  form {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }
`;

export const FieldWrap = styled.div`
  .control {
    position: relative;
    margin-top: 8px;
  }
`;

export const FieldLabel = styled.label`
  && {
    display: block;
    margin: 0;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    color: var(--auth-foreground);
  }
`;

export const FieldInput = styled.input`
  && {
    display: block;
    width: 100%;
    height: 48px;
    margin: 0;
    padding: 0 ${({ $hasToggle }) => ($hasToggle ? '44px' : '16px')} 0 16px;
    border: 1px solid ${({ $invalid }) => ($invalid ? 'var(--danger)' : 'var(--auth-border)')};
    border-radius: 18px;
    background: var(--auth-card);
    font-family: inherit;
    font-size: 14px;
    line-height: 20px;
    color: var(--auth-foreground);
    outline: none;
    box-shadow: none;
    transition: box-shadow 0.15s ease;

    &::placeholder {
      color: var(--auth-muted-foreground);
      opacity: 1;
    }

    &:focus {
      box-shadow: 0 0 0 2px color-mix(in oklab, var(--auth-houdiny-from) 25%, transparent);
    }
  }
`;

/* One box per digit of a verification code (see CodeInput). */
export const CodeBoxes = styled.div`
  display: flex;
  gap: 12px;

  /* Classed, so the app's global input rules cannot tie with it. */
  input.code-digit {
    width: 56px;
    height: 60px;
    margin: 0;
    padding: 0;
    border: 1px solid ${({ $invalid }) => ($invalid ? 'var(--danger)' : 'var(--auth-border)')};
    border-radius: 18px;
    background: var(--auth-card);
    font-family: inherit;
    font-size: 24px;
    font-weight: 600;
    line-height: 1;
    text-align: center;
    font-variant-numeric: tabular-nums;
    color: var(--auth-foreground);
    caret-color: var(--auth-primary);
    outline: none;
    box-shadow: var(--auth-shadow-card);
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;

    &.filled {
      border-color: ${({ $invalid }) =>
        $invalid ? 'var(--danger)' : 'color-mix(in oklab, var(--auth-primary) 45%, var(--auth-border))'};
    }

    &:focus {
      border-color: var(--auth-primary);
      box-shadow: 0 0 0 3px color-mix(in oklab, var(--auth-houdiny-from) 22%, transparent);
    }

    &:disabled {
      opacity: 0.6;
    }

    @media (max-width: 380px) {
      width: 48px;
      height: 54px;
      font-size: 22px;
    }
  }
`;

export const PasswordToggle = styled.button`
  && {
    position: absolute;
    top: 50%;
    right: 12px;
    display: flex;
    align-items: center;
    padding: 0;
    border: none;
    background: none;
    color: var(--auth-muted-foreground);
    transform: translateY(-50%);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover {
      color: var(--auth-foreground);
    }

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export const FieldHint = styled.p`
  && {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: var(--auth-muted-foreground);
  }
`;

export const FieldError = styled.p`
  && {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 16px;
    color: var(--danger);
  }
`;

export const ForgotLink = styled(Link)`
  && {
    align-self: flex-end;
    margin-top: -8px;
    font-size: 13px;
    font-weight: 500;
    color: var(--auth-primary);

    &:hover {
      text-decoration: underline;
      color: var(--auth-primary);
    }
  }
`;

export const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  border: none;
  border-radius: 18px;
  background-image: var(--auth-gradient-houdiny);
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  opacity: 0.75;
  box-shadow: var(--auth-shadow-card);
  cursor: pointer;
  transition:
    transform 0.15s ease,
    opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: scale(1.01);
    opacity: 1;
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export const Spinner = styled.span`
  display: inline-flex;
  animation: ${spin} 1s linear infinite;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const SwitchText = styled.p`
  && {
    margin: 32px 0 0;
    font-size: 14px;
    color: var(--auth-muted-foreground);

    /* A button where the action is not a plain navigation (Start over signs
       out first), drawn exactly like the links. */
    a,
    .link {
      padding: 0;
      border: none;
      font: inherit;
      font-weight: 500;
      color: var(--auth-primary);
      background: none;
      cursor: pointer;

      &:hover {
        text-decoration: underline;
        color: var(--auth-primary);
      }
    }

    /* "Start over ->": the arrow nudges on hover instead of an underline. */
    .with-arrow {
      display: inline-flex;
      align-items: center;
      gap: 6px;

      svg {
        width: 15px;
        height: 15px;
        transition: transform 0.15s ease;
      }

      &:hover {
        text-decoration: none;

        svg {
          transform: translateX(2px);
        }
      }
    }
  }
`;

/* ---------------- Confirm email screens ---------------- */

export const IconTile = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin-top: 36px;
  border-radius: 22px;
  background-image: var(--auth-gradient-soft);
  color: ${({ $danger }) => ($danger ? 'var(--danger)' : 'var(--auth-primary)')};

  svg {
    width: 20px;
    height: 20px;
  }
`;

export const VerifyTitle = styled(AuthTitle)`
  && {
    margin-top: 24px;
  }
`;

export const Emphasis = styled.span`
  font-weight: 500;
  color: var(--auth-foreground);
  word-break: break-word;
`;

export const InfoCard = styled.div`
  margin-top: ${({ $mt }) => $mt || '28px'};
  padding: 20px;
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  background: var(--auth-card);
  box-shadow: var(--auth-shadow-card);
`;

export const StepList = styled.ol`
  && {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 0;
    padding: 0;
    list-style: none;
    font-size: 14px;
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
  }

  .num {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-top: 2px;
    border-radius: 9999px;
    background-image: var(--auth-gradient-soft);
    font-size: 12px;
    font-weight: 600;
    color: var(--auth-primary);
  }

  /* Not .text: GlobalStyles sets every .text to 20px. */
  .step-text {
    line-height: 1.625;
    color: var(--auth-muted-foreground);
  }
`;

export const CardTitle = styled.p`
  && {
    margin: 0;
    font-size: 14px;
    line-height: 20px;
    font-weight: 500;
    color: var(--auth-foreground);
  }
`;

export const CardText = styled.p`
  && {
    margin: 4px 0 0;
    font-size: 12px;
    line-height: 1.625;
    color: var(--auth-muted-foreground);
  }
`;

export const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 16px;

  .note {
    font-size: 12px;
    color: var(--auth-muted-foreground);
  }
`;

export const OutlineButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    height: 40px;
    padding: 0 16px;
    border: 1px solid var(--auth-border);
    border-radius: 18px;
    background: var(--auth-card);
    font-size: 14px;
    font-weight: 500;
    color: var(--auth-foreground);
    cursor: pointer;
    transition: background-color 0.15s ease;

    &:hover:not(:disabled) {
      background: var(--auth-muted);
    }
  }
`;

export const GradientButton = styled.button`
  && {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: ${({ $block }) => ($block ? '48px' : '40px')};
    width: ${({ $block }) => ($block ? '100%' : 'auto')};
    margin-top: ${({ $block }) => ($block ? '24px' : '0')};
    padding: 0 16px;
    border: none;
    border-radius: 18px;
    background-image: var(--auth-gradient-houdiny);
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    box-shadow: var(--auth-shadow-card);
    cursor: pointer;
    transition: transform 0.15s ease;

    &:hover:not(:disabled) {
      transform: scale(1.01);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
`;

/* ---------------- Connect LinkedIn step ---------------- */

export const AssuranceList = styled.ul`
  && {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin: 32px 0 0;
    padding: 20px;
    list-style: none;
    border: 1px solid var(--auth-border);
    border-radius: 22px;
    background: var(--auth-card);
    box-shadow: var(--auth-shadow-card);
  }

  li {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    font-size: 14px;
    line-height: 20px;
    color: var(--auth-muted-foreground);
  }

  .icon {
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    margin-top: 2px;
    border-radius: 14px;
    background-image: var(--auth-gradient-soft);
    color: var(--auth-primary);

    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

export const TextButton = styled.button`
  && {
    display: inline-block;
    margin-top: ${({ $mt }) => $mt || '24px'};
    padding: 0;
    border: none;
    background: none;
    font-size: 14px;
    font-weight: 500;
    color: var(--auth-muted-foreground);
    cursor: pointer;
    transition: color 0.15s ease;

    &:hover:not(:disabled) {
      color: var(--auth-foreground);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
`;

export const StatusCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 32px;
  padding: 28px 20px;
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  background: var(--auth-card);
  box-shadow: var(--auth-shadow-card);
  text-align: center;

  strong {
    font-size: 14px;
    font-weight: 600;
    color: var(--auth-foreground);
  }

  small {
    max-width: 340px;
    font-size: 12px;
    line-height: 1.6;
    color: var(--auth-muted-foreground);
  }

  .spin {
    display: inline-flex;
    color: var(--auth-primary);
    animation: ${spin} 1s linear infinite;

    svg {
      width: 24px;
      height: 24px;
    }
  }

  .ok svg {
    width: 44px;
    height: 44px;
    color: #16a34a;
  }

  ${CardActions} {
    justify-content: center;
    margin-top: 8px;
  }
`;

export const FormError = styled.p`
  && {
    margin: 16px 0 0;
    font-size: 13px;
    line-height: 18px;
    color: var(--danger);
  }
`;
